<template>
  <CenteredPageContainer :class="$style.page">
    <BreadcrumbList :links="breadcrumbs" />
    <section :class="[$style.page__section, $style.section]">
      <SectionTitle :class="$style.section__title">月間サマリ</SectionTitle>
      <section :class="[$style.section__body, $style.body]">
        <FadeTransition>
          <div :class="$style.body__message" v-if="isLoading">
            <LoadingIndicator />
          </div>
          <MatchSummary
            :class="$style.body__summary"
            :statuses="statuses"
            :planners="planners"
            v-model:filteringPlannerId="filteringPlannerId"
            v-model:selectedMonth="selectedMonth"
            v-else
          />
        </FadeTransition>
      </section>
    </section>
  </CenteredPageContainer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import MatchSummary from './MatchSummary/MatchSummary.vue'
import { addMonths, format, parse, startOfMonth } from 'date-fns'
import {
  useMembersReportPageForBranchLazyQuery,
  useMembersReportPageForCorporationLazyQuery,
  useMembersReportPageQueryQuery,
  type MembersReportPageForBranchQuery,
  type MembersReportPageForCorporationQuery,
  type MembersReportPagePlannerFragment,
  type MembersReportPageMatcuStatusFragment,
} from './MembersReportPage.generated'

import { type Account, type BreadCrumbLink, type PlannerID } from '@/types/app'
import { useRoute, useRouter } from 'vue-router'
import { getQuery } from '@/libs/utilities/getQuery'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException } from '@sentry/vue'
import { useHead } from '@unhead/vue'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import { parseDateString } from '@/libs/utilities/gql-scalar'
import { useFetchPlannerOnChatPageQuery } from '@/pages/ChatPage/ChatPageContainer.generated.ts'
import { parsePlannerProfileResponseToAccount } from '@/apis/repositories/parsers/planner.ts'

const today = new Date()
const thisMonth = format(today, 'yyyy-MM-01')

useHead({
  title: '社内レポート',
})

const isLoading = ref(true)
const statuses = ref<readonly MembersReportPageMatcuStatusFragment[]>([])
const planners = ref<readonly MembersReportPagePlannerFragment[]>([])

const { result: reportPageQueryResult } = useMembersReportPageQueryQuery()

const router = useRouter()

const createFetcher =
  (fetchFunction: () => Promise<void>): (() => Promise<void>) =>
  async () => {
    isLoading.value = true
    try {
      await fetchFunction()
    } catch (error) {
      console.log(error)
      if (!isClientError(error)) {
        captureException(error)
        // eslint-disable-next-line fp/no-mutating-methods
        await router.push({ name: 'errorInternalServerError' })
        return
      }
      // eslint-disable-next-line fp/no-mutating-methods
      await router.push({ name: 'errorForbidden' })
    } finally {
      isLoading.value = false
    }
  }

const route = useRoute('membersReport')

const breadcrumbs = computed(
  (): ReadonlyArray<BreadCrumbLink> => route.meta.breadcrumbs ?? []
)

const filteringPlannerId = computed({
  get(): Maybe<PlannerID> {
    const plannerId = route.query['plannerId']
    if (typeof plannerId === 'string') {
      return Number.parseInt(plannerId)
    }
    return undefined
  },
  set(plannerId: Maybe<PlannerID>): void {
    // eslint-disable-next-line fp/no-mutating-methods
    void router.push({ query: { ...route.query, plannerId } })
  },
})

const selectedMonth = computed({
  get(): string {
    return getQuery(route.query.month) ?? thisMonth
  },
  set(month: string): void {
    // eslint-disable-next-line fp/no-mutating-methods
    void router.push({ query: { ...route.query, month } })
  },
})

const since = computed(() => {
  return startOfMonth(parse(selectedMonth.value, 'yyyy-MM-dd', new Date()))
})

const until = computed(() => {
  return startOfMonth(addMonths(since.value, 1))
})

const { result } = useFetchPlannerOnChatPageQuery()
const account = computed<Account | undefined>(() => {
  const data = result.value?.planner
  if (data) {
    return parsePlannerProfileResponseToAccount({
      ...data,
      id: parseInt(data.id),
      corporationId: parseInt(data.corporationId),
      branchIds: data.branchIds.map(id => parseInt(id)),
      specialties: data.specialties.map(specialty => specialty.name),
    })
  }
  return undefined
})

const { load: loadCorporation, refetch: refetchCorporation } =
  useMembersReportPageForCorporationLazyQuery({
    since: parseDateString(since.value),
    until: parseDateString(until.value),
  })

const { load: loadBranch, refetch: refetchBranch } =
  useMembersReportPageForBranchLazyQuery({
    since: parseDateString(since.value),
    until: parseDateString(until.value),
  })

const fetchReport = computed((): (() => Promise<void>) => {
  const canViewCorporationReport =
    reportPageQueryResult.value?.uiControl.viewCorporationReport ?? false
  if (
    !canViewCorporationReport &&
    !(reportPageQueryResult.value?.uiControl.viewBranchReport ?? false)
  ) {
    // For return value type consistency
    return async () => {
      // eslint-disable-next-line fp/no-mutating-methods
      await router.push({ name: 'errorForbidden' })
      // do nothing
    }
  }

  if (canViewCorporationReport) {
    return createFetcher(async () => {
      const res = await (async () => {
        const data = await loadCorporation(null, {
          since: parseDateString(since.value),
          until: parseDateString(until.value),
        })

        if (data) {
          return data
        }

        const result = await refetchCorporation({
          since: parseDateString(since.value),
          until: parseDateString(until.value),
        })

        return result?.data
      })()

      if (!res) {
        return
      }

      planners.value = res.plannersInSameCorporation

      defineResponseHandlers<
        MembersReportPageForCorporationQuery['matchStatusesInSameCorporation'],
        void
      >({
        MatchStatusesInSameCorporationErrorNotAuthorityPayload: () => {
          // eslint-disable-next-line fp/no-mutating-methods
          router.push({ name: 'errorForbidden' })
        },
        MatchStatusesInSameCorporationSuccessPayload: payload => {
          statuses.value = payload.matchStatuses
        },
      })(res.matchStatusesInSameCorporation)
    })
  }

  return createFetcher(async () => {
    const res = await (async () => {
      const data = await loadBranch(null, {
        since: parseDateString(since.value),
        until: parseDateString(until.value),
      })

      if (data) {
        return data
      }

      const result = await refetchBranch({
        since: parseDateString(since.value),
        until: parseDateString(until.value),
      })

      return result?.data
    })()

    if (!res) {
      return
    }

    planners.value = res.plannersInSameBranch

    defineResponseHandlers<
      MembersReportPageForBranchQuery['matchStatusesInSameBranch'],
      void
    >({
      MatchStatusesInSameBranchErrorNotAuthorityPayload: () => {
        // eslint-disable-next-line fp/no-mutating-methods
        router.push({ name: 'errorForbidden' })
      },
      MatchStatusesInSameBranchSuccessPayload: payload => {
        statuses.value = payload.matchStatuses
      },
    })(res.matchStatusesInSameBranch)
  })
})

const isFetcherReady = computed((): boolean => !!account.value)
watch(
  [isFetcherReady, selectedMonth],
  async ([isReady, _month]) => {
    if (isReady) {
      await fetchReport.value()
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" module>
.page {
  &__section {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.section {
  &__title {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.body {
  padding: 24px;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-24;

  &__message {
    display: flex;
    min-height: 40px;
    align-items: center;
    justify-content: center;
  }
}
</style>
